// Minimal ActionCable-compatible client using the native WebSocket API.
// No external deps to keep things lightweight and offline-friendly.

export interface CableMessage {
  type?: string
  identifier?: string
  message?: unknown
}

interface SubscriptionCallbacks {
  connected?: () => void
  rejected?: () => void
  disconnected?: (ev?: CloseEvent | Event) => void
  received?: (data: unknown) => void
}

export interface Subscription {
  unsubscribe: () => void
  perform: (action: string, data?: Record<string, unknown>) => void
}

export class Cable {
  private ws?: WebSocket
  private url: string
  private reconnect: boolean
  private subscriptions = new Map<string, SubscriptionCallbacks>()
  private reopenTimer: number | null = null

  constructor(url: string, { reconnect = true }: { reconnect?: boolean } = {}) {
    this.url = toWsUrl(url)
    this.reconnect = reconnect
    // no-op
  }

  open() {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    )
      return
    try {
      this.ws = new WebSocket(this.url)
    } catch {
      // swallow, we'll retry below
      this.scheduleReconnect()
      return
    }

    this.ws.addEventListener("open", () => {
      // Send all pending subscriptions when connection opens
      this.subscriptions.forEach((_, ident) =>
        this.send({ command: "subscribe", identifier: ident }),
      )
    })
    this.ws.addEventListener("close", (ev) => {
      // disconnected
      // notify all subs that we disconnected
      this.subscriptions.forEach((cbs) => cbs.disconnected?.(ev))
      if (this.reconnect) this.scheduleReconnect()
    })
    this.ws.addEventListener("error", () => {
      // let close handler manage reconnection
    })
    this.ws.addEventListener("message", (evt) => this.handleMessage(evt))
  }

  private scheduleReconnect() {
    if (!this.reconnect) return
    if (this.reopenTimer) return
    this.reopenTimer = window.setTimeout(() => {
      this.reopenTimer = null
      this.open()
      // resubscribe existing identifiers
      this.subscriptions.forEach((_, ident) =>
        this.send({ command: "subscribe", identifier: ident }),
      )
    }, 1000)
  }

  private handleMessage(evt: MessageEvent) {
    try {
      const text = typeof evt.data === "string" ? evt.data : String(evt.data)
      const data = JSON.parse(text) as CableMessage
      // Types we may see: welcome, ping, confirm_subscription, reject_subscription
      if (
        data.type === "welcome" ||
        data.type === "ping" ||
        data.type === "disconnect"
      ) {
        return
      }
      if (data.type === "confirm_subscription" && data.identifier) {
        this.subscriptions.get(data.identifier)?.connected?.()
        return
      }
      if (data.type === "reject_subscription" && data.identifier) {
        this.subscriptions.get(data.identifier)?.rejected?.()
        return
      }
      if (
        data.identifier &&
        Object.prototype.hasOwnProperty.call(data, "message")
      ) {
        this.subscriptions.get(data.identifier)?.received?.(data.message)
        return
      }
    } catch {
      // ignore malformed frames
    }
  }

  private send(payload: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify(payload))
  }

  subscribe(
    channel: string,
    params: Record<string, unknown>,
    callbacks: SubscriptionCallbacks = {},
  ): Subscription {
    // The identifier must be a JSON string per ActionCable protocol
    const identifier = JSON.stringify({ channel, ...params })
    this.subscriptions.set(identifier, callbacks)
    // ensure connection is open
    this.open()
    // send subscribe command immediately if connection is already open
    // otherwise, the 'open' event handler will send all pending subscriptions
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({ command: "subscribe", identifier })
    }

    return {
      unsubscribe: () => {
        this.send({ command: "unsubscribe", identifier })
        this.subscriptions.delete(identifier)
      },
      perform: (action: string, data: Record<string, unknown> = {}) => {
        // Align with ActionCable perform contract
        this.send({
          command: "message",
          identifier,
          data: JSON.stringify({ action, ...data }),
        })
      },
    }
  }
}

export function toWsUrl(pathOrUrl: string) {
  // Accept absolute ws(s)://, absolute http(s)://, or relative /cable paths
  if (/^wss?:\/\//i.test(pathOrUrl)) return pathOrUrl
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl.replace(/^http/i, "ws")
  const origin = window.location.origin
  const base = origin.replace(/^http/i, "ws")
  return pathOrUrl.startsWith("/") ? base + pathOrUrl : `${base}/${pathOrUrl}`
}
